import { ProjectModel, type SchedulerPro } from '@bryntum/schedulerpro'
import { subscribe } from 'valtio/vanilla'

import {
  selectSchedulerEventsAsArray,
  selectSchedulerResourcesAsArray,
} from '@/features/domain/scheduler'

import { domainProxy, store } from '@/store'
import { schedulerPreferencesAtom, setSchedulerPreferences } from '../schedulerPreferences'

import { syncApprovedRoutesVisibility } from './sync/approvedRoutes'
import { syncProjectData } from './sync/data'
import { syncProjectFilter } from './sync/filter'
import { syncProjectGrouping } from './sync/grouping'
import { syncProjectSorting } from './sync/sorting'
import { computeTimeRanges, syncProjectTimeRanges } from './sync/timeRange'
import { syncUnavailableRoutesVisibility } from './sync/unavailableRoutes'

import { approvedRouteFilter, unavailableRouteFilter } from './filters'
import { dateGrouper, vehicleGrouper } from './groupers'

export type SchedulerInstance = {
  instance?: SchedulerPro
}

// -----------------------------------
// Scheduler atom
// ------------------------------------

export const project = new ProjectModel({
  events: selectSchedulerEventsAsArray(store.getState()),
  resources: selectSchedulerResourcesAsArray(store.getState()),
  timeRanges: computeTimeRanges(),

  eventStore: {
    syncDataOnLoad: true,
    useRawData: true,
    filters: schedulerPreferencesAtom.preferences.showApprovedRoutes ? [] : [approvedRouteFilter],
  },

  resourceStore: {
    reapplyFilterOnUpdate: true,
    reapplyFilterOnAdd: true,
    syncDataOnLoad: true,
    useRawData: true,
    onSort: ({ source }) => {
      if (source.sorters.length === 0) return

      setSchedulerPreferences({
        sorters: source.sorters.reduce<{ field: string; ascending: boolean }[]>((acc, sorter) => {
          if (sorter.field === undefined || sorter.ascending === undefined) return acc
          acc.push({ field: sorter.field, ascending: sorter.ascending })

          return acc
        }, []),
      })
    },
    filters: schedulerPreferencesAtom.preferences.showUnavailableRoutes
      ? []
      : [unavailableRouteFilter],
    groupers: [
      schedulerPreferencesAtom.preferences.groupBy === 'vehicleId' ? vehicleGrouper : dateGrouper,
    ],
    // Commented out because it is not used in the current implementation
    // sorters are applied via the onSort function

    // sorters: [
    //   {
    //     ...schedulerPreferencesAtom.preferences.sorters[0],
    //     fn: getSorterByField(schedulerPreferencesAtom.preferences.sorters[0].field),
    //   },
    // ],
  },

  timeRangeStore: {
    useRawData: true,
  },
})

export const schedulerInstance: SchedulerInstance = {
  instance: undefined,
}

// ------------------------------------
// Read functions
// ------------------------------------

export function getProject() {
  return project
}

export function subscribeSchedulerProject() {
  // start listening to server-data events
  const unsubscribeDataChange = domainProxy.subscribeToDataChangeSuccess(domainAction => {
    // Suspend refresh to avoid multiple refreshes
    schedulerInstance.instance?.suspendRefresh()

    // SYNC
    syncProjectTimeRanges()
    syncProjectFilter()
    syncProjectData(domainAction)

    // Resume refresh
    schedulerInstance.instance?.resumeRefresh(true)
  })

  const unsubscribePreferences = subscribe(schedulerPreferencesAtom, () => {
    // Suspend refresh to avoid multiple refreshes
    schedulerInstance.instance?.suspendRefresh()

    // SYNC
    syncProjectSorting()
    syncProjectGrouping()
    syncApprovedRoutesVisibility()
    syncUnavailableRoutesVisibility()

    // Resume refresh
    schedulerInstance.instance?.resumeRefresh(true)
  })

  return () => {
    unsubscribeDataChange()
    unsubscribePreferences()
  }
}
